export const RESET_PASSWORD_PATH = '/reset-password'
export const LEGACY_PASSWORD_RECOVERY_PATH = '/password-recovery'

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '')
const trimLeadingSlash = (value) => String(value || '').replace(/^\/+/, '')

const normalizePath = (value) => {
  const trimmed = String(value || '').trim()
  if (!trimmed || trimmed === '/') return ''
  return `/${trimLeadingSlash(trimTrailingSlash(trimmed))}`
}

const getAbsoluteUrlOrigin = (value) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''

  try {
    return trimTrailingSlash(new URL(trimmed).origin)
  } catch {
    return ''
  }
}

const getAbsoluteUrl = (value) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return null

  try {
    return new URL(trimmed)
  } catch {
    return null
  }
}

const deriveBasePathFromRecoveryUrlPathname = (pathname) => {
  const normalizedPath = normalizePath(pathname)
  if (!normalizedPath) return ''

  if (normalizedPath === RESET_PASSWORD_PATH || normalizedPath === LEGACY_PASSWORD_RECOVERY_PATH) {
    return ''
  }

  if (normalizedPath.endsWith(RESET_PASSWORD_PATH)) {
    return normalizePath(normalizedPath.slice(0, -RESET_PASSWORD_PATH.length))
  }

  if (normalizedPath.endsWith(LEGACY_PASSWORD_RECOVERY_PATH)) {
    return normalizePath(normalizedPath.slice(0, -LEGACY_PASSWORD_RECOVERY_PATH.length))
  }

  return ''
}

export const getAppBasePath = () => {
  const configuredBasePath = normalizePath(process.env.REACT_APP_BASE_PATH)
  if (configuredBasePath) {
    return configuredBasePath
  }

  const configuredRecoveryUrl = getAbsoluteUrl(process.env.REACT_APP_AUTH_RECOVERY_REDIRECT_URL)
  if (configuredRecoveryUrl) {
    const derivedBasePath = deriveBasePathFromRecoveryUrlPathname(configuredRecoveryUrl.pathname)
    if (derivedBasePath) {
      return derivedBasePath
    }
  }

  return ''
}

export const getAppOrigin = () => {
  const configuredRecoveryUrl = process.env.REACT_APP_AUTH_RECOVERY_REDIRECT_URL?.trim()
  if (configuredRecoveryUrl) {
    const configuredRecoveryOrigin = getAbsoluteUrlOrigin(configuredRecoveryUrl)
    if (configuredRecoveryOrigin) {
      return configuredRecoveryOrigin
    }
  }

  const configuredAppUrl = process.env.REACT_APP_APP_URL?.trim()
  if (configuredAppUrl) {
    const configuredAppOrigin = getAbsoluteUrlOrigin(configuredAppUrl)
    if (configuredAppOrigin) {
      return configuredAppOrigin
    }
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return trimTrailingSlash(window.location.origin)
  }

  return ''
}

const buildRedirectUrl = (origin, basePath, pathname) => {
  const normalizedBasePath = normalizePath(basePath)
  const normalizedPathname = `/${trimLeadingSlash(pathname)}`
  return `${trimTrailingSlash(origin)}${normalizedBasePath}${normalizedPathname}`
}

const parseUrlParams = (value) => {
  const raw = String(value || '').trim().replace(/^[#?]/, '')
  return new URLSearchParams(raw)
}

const hasRecoverySignal = (params) => {
  const recoveryType = String(params.get('type') || '').trim().toLowerCase()
  return (
    recoveryType === 'recovery' ||
    params.has('token_hash') ||
    (recoveryType === 'recovery' && (params.has('access_token') || params.has('refresh_token')))
  )
}

export const getPasswordResetRedirectUrl = () => {
  const configuredRecoveryUrl = getAbsoluteUrl(process.env.REACT_APP_AUTH_RECOVERY_REDIRECT_URL)
  if (configuredRecoveryUrl) {
    const origin = trimTrailingSlash(configuredRecoveryUrl.origin)
    const basePath = getAppBasePath()
    return buildRedirectUrl(origin, basePath, RESET_PASSWORD_PATH)
  }

  return buildRedirectUrl(getAppOrigin(), getAppBasePath(), RESET_PASSWORD_PATH)
}

export const isPasswordRecoveryCallbackUrl = (locationLike = null) => {
  const candidate =
    locationLike ||
    (typeof window !== 'undefined' && window.location ? window.location : null)

  if (!candidate) return false

  return hasRecoverySignal(parseUrlParams(candidate.search)) || hasRecoverySignal(parseUrlParams(candidate.hash))
}
